/**
 * A party's ledger, as the workbook an accountant expects.
 *
 * What we billed them sits in Debit, what they paid sits in Credit, in date
 * order, and a balance runs down the side so the sheet says what was owed on
 * any given day rather than only at the end. Every figure comes from the
 * invoices and the payment records exactly as the server holds them; the only
 * arithmetic here is the running total and the two sums under it.
 *
 * A creditor's account is the same sheet with the sides swapped — their bill
 * is a credit, what we paid them is a debit — which is why `kind` decides the
 * headings and the direction the balance runs.
 */

const MONEY = '#,##0.00';
const NAVY = 'FF1F3864';
const HEAD_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
const SOFT_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F5FA' } };
const BORDER = { style: 'thin', color: { argb: 'FFBFBFBF' } };
const BOX = { top: BORDER, left: BORDER, bottom: BORDER, right: BORDER };

const RED = 'FFC00000';
const AMBER = 'FFB26B00';
const GREEN = 'FF1F7A44';

/** `2026-10-07` → `07/10/2026`, which is how the rest of the app prints a date. */
const asDate = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso || '');
};

const today = () => asDate(new Date().toISOString());

export async function exportLedgerExcel({
  partyName, kind = 'customer', entries, totalDebit, totalCredit, closing = 0, unapplied = 0, open = [],
}) {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Ledger');

  const creditor = kind === 'creditor';
  const owedLabel = creditor ? 'Balance payable' : 'Balance due';
  const theirBill = creditor ? 'Purchase bill' : 'Invoice';

  sheet.columns = [
    { key: 'date', width: 13 },
    { key: 'what', width: 20 },
    { key: 'number', width: 26 },
    { key: 'debit', width: 15, style: { numFmt: MONEY } },
    { key: 'credit', width: 15, style: { numFmt: MONEY } },
    { key: 'balance', width: 16, style: { numFmt: MONEY } },
    { key: 'tag', width: 26 },
  ];
  const LAST = 7;

  const wide = (row, text, opts = {}) => {
    sheet.mergeCells(row.number, 1, row.number, LAST);
    row.getCell(1).value = text;
    row.getCell(1).alignment = { horizontal: opts.align || 'center' };
    if (opts.font) row.getCell(1).font = opts.font;
    return row;
  };

  /* ── whose account, and as of when ─────────────────────────────────────── */
  wide(sheet.addRow([]), 'LEDGER', { font: { bold: true, size: 15, color: { argb: NAVY } } });
  wide(sheet.addRow([]), partyName || '', { font: { bold: true, size: 12 } });
  wide(sheet.addRow([]), `${creditor ? 'Creditor' : 'Customer'} account · as on ${today()}`,
    { font: { size: 10, color: { argb: 'FF595959' } } });
  sheet.addRow([]);

  /* ── the three figures the sheet is read for ───────────────────────────── */
  const summary = [
    [creditor ? 'Billed by them' : 'Billed to them', creditor ? totalCredit : totalDebit],
    [creditor ? 'Paid to them' : 'Received from them', creditor ? totalDebit : totalCredit],
    [closing >= 0 ? owedLabel : 'Paid in excess', Math.abs(closing)],
  ];
  summary.forEach(([label, value], i) => {
    const row = sheet.addRow([]);
    sheet.mergeCells(row.number, 1, row.number, 3);
    sheet.mergeCells(row.number, 4, row.number, LAST);
    row.getCell(1).value = label;
    row.getCell(4).value = value;
    row.getCell(4).numFmt = MONEY;
    row.getCell(1).fill = SOFT_FILL;
    row.getCell(4).fill = SOFT_FILL;
    row.getCell(1).font = { bold: i === 2, size: 11 };
    row.getCell(4).font = {
      bold: true,
      size: i === 2 ? 12 : 11,
      color: i === 2 ? { argb: closing >= 0 ? RED : GREEN } : undefined,
    };
    row.getCell(4).alignment = { horizontal: 'right' };
    [1, 4].forEach((c) => { row.getCell(c).border = BOX; });
  });

  if (unapplied > 0) {
    // Money of theirs we are holding that has not been put against a bill yet;
    // it is not in the balance above, and saying so saves the question.
    const row = sheet.addRow([]);
    sheet.mergeCells(row.number, 1, row.number, 3);
    sheet.mergeCells(row.number, 4, row.number, LAST);
    row.getCell(1).value = creditor ? 'Paid in advance, not against a bill' : 'Received, not yet against an invoice';
    row.getCell(4).value = unapplied;
    row.getCell(4).numFmt = MONEY;
    row.getCell(4).alignment = { horizontal: 'right' };
    [1, 4].forEach((c) => {
      row.getCell(c).font = { italic: true, size: 10, color: { argb: 'FF595959' } };
      row.getCell(c).border = BOX;
    });
  }

  sheet.addRow([]);

  /* ── the account itself ────────────────────────────────────────────────── */
  const head = sheet.addRow(['Date', 'Particulars', `${theirBill} no`, 'Debit', 'Credit', 'Balance',
    creditor ? 'Bill status' : 'Invoice status']);
  head.eachCell((c) => {
    c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    c.fill = HEAD_FILL;
    c.alignment = { horizontal: 'center', vertical: 'middle' };
    c.border = BOX;
  });

  entries.forEach((e) => {
    // A side with nothing on it is left empty, not written as a zero.
    const row = sheet.addRow({
      date: asDate(e.date),
      what: e.note || '',
      number: e.number || '',
      debit: e.debit > 0 ? e.debit : null,
      credit: e.credit > 0 ? e.credit : null,
      balance: e.balance,
      // on a bill, where it stands; on a payment, which receipt it was part of
      tag: e.tag || '',
    });
    row.getCell('date').alignment = { horizontal: 'center' };
    row.getCell('balance').font = { bold: true };
    // A payment reads lighter than the bill it pays, so the two are told apart
    // down the page without reading the Particulars column.
    const paid = creditor ? e.debit > 0 : e.credit > 0;
    if (paid) row.getCell('what').font = { color: { argb: GREEN } };

    /* The status reads the same on a bill and on the payment that cleared it:
       it is the bill's standing either way, which is what the column says. */
    const still = e.due > 0.009;
    const part = still && e.tag && e.tag.startsWith('Part');
    row.getCell('tag').font = {
      bold: still,
      color: { argb: still ? (part ? AMBER : RED) : GREEN },
    };
    row.eachCell((c) => { c.border = BOX; });
  });

  const totals = sheet.addRow({
    date: '', what: 'Total', number: '', tag: '',
    debit: totalDebit > 0 ? totalDebit : null,
    credit: totalCredit > 0 ? totalCredit : null,
    balance: Math.abs(closing),
  });
  totals.font = { bold: true };
  totals.eachCell((c) => {
    c.border = { top: { style: 'double', color: { argb: NAVY } }, left: BORDER, bottom: BORDER, right: BORDER };
  });

  /* ── what of it is still open, named rather than left to be hunted ─────── */
  if (open.length) {
    sheet.addRow([]);
    const title = sheet.addRow([]);
    sheet.mergeCells(title.number, 1, title.number, LAST);
    title.getCell(1).value = `Still unpaid · ${open.length} ${creditor ? 'bill' : 'invoice'}${open.length === 1 ? '' : 's'}`;
    title.getCell(1).font = { bold: true, size: 11, color: { argb: NAVY } };

    const openHead = sheet.addRow(['Date', '', `${theirBill} no`, creditor ? 'Billed' : 'Invoiced', 'Paid', 'Still due', '']);
    openHead.eachCell((c, n) => {
      if (n === 2 || n === LAST) return;
      c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      c.fill = HEAD_FILL;
      c.alignment = { horizontal: 'center' };
      c.border = BOX;
    });

    open.forEach((b) => {
      const total = creditor ? b.credit : b.debit;
      const row = sheet.addRow({
        date: asDate(b.date), what: '', number: b.number,
        debit: total, credit: Math.round((total - b.due) * 100) / 100 || null, balance: b.due, tag: '',
      });
      row.getCell('date').alignment = { horizontal: 'center' };
      row.getCell('balance').font = { bold: true, color: { argb: RED } };
      [1, 3, 4, 5, 6].forEach((n) => { row.getCell(n).border = BOX; });
    });

    const openTotal = sheet.addRow({
      date: '', what: '', number: 'Total still due', debit: null, credit: null,
      balance: Math.round(open.reduce((t, b) => t + b.due, 0) * 100) / 100, tag: '',
    });
    openTotal.getCell(3).font = { bold: true };
    openTotal.getCell(6).font = { bold: true, color: { argb: RED } };
    [3, 4, 5, 6].forEach((n) => {
      openTotal.getCell(n).border = { top: { style: 'double', color: { argb: NAVY } }, left: BORDER, bottom: BORDER, right: BORDER };
    });
  }

  /* ── what it all comes to, said once more in words ─────────────────────── */
  sheet.addRow([]);
  const foot = sheet.addRow([]);
  sheet.mergeCells(foot.number, 1, foot.number, 3);
  sheet.mergeCells(foot.number, 4, foot.number, LAST);
  foot.getCell(1).value = closing >= 0
    ? `${owedLabel} as on ${today()}`
    : `Paid in excess as on ${today()}`;
  foot.getCell(4).value = Math.abs(closing);
  foot.getCell(4).numFmt = MONEY;
  foot.getCell(4).alignment = { horizontal: 'right' };
  [1, 4].forEach((c) => {
    foot.getCell(c).font = { bold: true, size: 12, color: { argb: closing >= 0 ? RED : GREEN } };
  });

  sheet.views = [{ state: 'frozen', ySplit: head.number }];

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  const safe = String(partyName || kind).replace(/[^\w]+/g, '_').replace(/^_|_$/g, '');
  link.download = `ledger_${safe}_${new Date().toISOString().split('T')[0]}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

export default exportLedgerExcel;
